import React, { useCallback, useContext } from "react";
import { type GetStaticProps, type GetStaticPaths } from "next";
import Image from "next/image";
import textSizeIcon from "@public/icon/text-size.svg";
import Link from "next/dist/client/link";
import Layout from "~/components/Layout";
import Print from "~/components/Article/Print";
import Share from "~/components/Article/Share";
import { FontSizeContext, fontSizes } from "~/components/FontSizeContext";
import { PostNoIndex } from "~/lib/db/post";
import useLoginURL from "@hooks/useLoginURL";
import { useSession } from "next-auth/react";
import PostContent from "~/components/PostContentComponent/index";
import useHighlightedPost from "@hooks/useHighlightedPost";
import {
  creaHtmlPost,
  createNote,
  setProtectedUrlPDF,
} from "~/utils/postUtils";
import FeatPost from "~/components/FeatPost";
import { useRouter } from "next/router";
import LoadingPost from "~/components/LoadingPost";
import { getLastArticle } from "~/lib/db/posts";
import { getPostInfoBySlug } from "~/lib/buildPost";
import { type ArticleReturnType } from "~/lib/db/types";
import { type Poll } from "~/lib/db/poll";
import { getPollsFromContent } from "~/lib/polls";
import CategoryArticle from "~/components/CategoryArticle";
import pdf from "@public/icon/ico-pdf.svg";

const textPreviewRule = {
  backgroundImage:
    "linear-gradient(180deg, rgba(61,68,77,1) 0%, rgba(255,255,255,0) 100%)",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
};
export default function PostPage({
  post,
  restrictAccess = false,
  noIndex,
  polls = [],
  hiddenDescription = false,
}: {
  post: ArticleReturnType;
  restrictAccess?: boolean;
  noIndex: string;
  polls: Poll[];
  hiddenDescription: boolean;
}) {
  const session = useSession();
  const isAuthenticated = session?.status === "authenticated";
  const router = useRouter();
  const { fontSize, setFontSize } = useContext(FontSizeContext);
  const loginURL = useLoginURL();
  const [highlightedPost] = useHighlightedPost(post, restrictAccess);
  const note = Boolean(post?.meta?.footnotes || post?.post_content)
    ? createNote(post)
    : null;
  const rotateFontSizes = useCallback(() => {
    const currentFontIndex = fontSizes.findIndex((sizes) => sizes === fontSize);

    const nextIndex = (currentFontIndex + 1) % fontSizes.length;

    setFontSize(fontSizes[nextIndex] as string);
  }, [fontSize, setFontSize]);
  const queryRouterString = router?.query?.q
    ? `?q=${router?.query?.q}${
        router?.query?.fields ? `&fields=${router?.query?.fields}` : ""
      }${
        router?.query?.searchType
          ? `&searchType=${router?.query?.searchType}`
          : ""
      }`
    : "/";
  if (router.isFallback || post === undefined) return <LoadingPost />;
  return (
    <>
      <Layout
        title={post?.meta?.rank_math_title || post.post_title}
        description={
          post?.meta?.rank_math_description ||
          post?.meta?.sottotitolo ||
          post?.post_excerpt
        }
        imageURL={post.image.url}
        imageCaption={post.image.caption}
        updatedTime={post.issue.date}
        robotsContent={noIndex}
      >
        <section className="-mt-12 w-full pb-20 print:pb-0">
          <div className="mx-auto max-w-section py-4 max-xl:px-4 max-xl:pt-20 xl:pb-12 xl:pt-52 print:max-w-none print:px-4 print:pb-0 print:pt-12">
            <div className="mb-12 flex max-lg:flex-wrap-reverse sm:mb-20 sm:text-xl lg:justify-between print:mb-6">
              <div>
                <div className="mb-4 flex flex-col gap-2 text-lg sm:mb-9 sm:text-xl md:text-[22px] print:mb-4 print:text-lg">
                  <CategoryArticle category={post.speciali} />
                  <CategoryArticle category={post.category} />
                </div>

                <span className="text-lg uppercase md:text-2xl print:text-lg">
                  {post.post_status != "future" && post.issue.slug ? (
                    <Link
                      className="font-bold text-orange"
                      href={`/n/${post.issue.slug}`}
                    >
                      BS{" "}
                      {post?.issue?.number &&
                      post?.issue?.number?.startsWith("0")
                        ? post?.issue?.number?.substring(1)
                        : post.issue.number}
                    </Link>
                  ) : (
                    <span className="font-bold text-orange">
                      BS{" "}
                      {post?.issue?.number &&
                      post?.issue?.number.startsWith("0")
                        ? post?.issue?.number?.substring(1)
                        : post?.issue?.number}
                    </span>
                  )}
                  {post.issue.date && (
                    <span className="font-light"> / {post.issue.date}</span>
                  )}
                </span>
              </div>
              <div className="flex items-center justify-end gap-6 max-lg:mb-12 max-lg:w-full md:gap-10 print:hidden">
                {isAuthenticated && post?.meta?.pdf && (
                  <a
                    id="export-pdf"
                    target="_blank"
                    className="flex items-center gap-2.5 hover:cursor-pointer hover:underline"
                    href={`/api/downloadPdf?id=${post?.meta?.pdf}`}
                  >
                    <Image src={pdf} alt="PDF" />
                    <span className="hidden text-base sm:block">PDF</span>
                  </a>
                )}

                <Share title={post.post_title.replace(/<\/?[^>]+(>|$)/g, "")} />
                <Print />
              </div>
            </div>
            <h1
              className="my-10 text-3xl font-thin md:text-5xl lg:text-6xl print:text-4xl"
              dangerouslySetInnerHTML={{ __html: highlightedPost.post_title }}
            ></h1>
            <div>
              {post?.meta?.sottotitolo && (
                <h3 className="mt-6 text-xl font-light md:text-2xl print:text-2xl">
                  {post?.meta?.sottotitolo}
                </h3>
              )}
              {post?.meta?.autore && (
                <h4 className="text-xl font-semibold text-neutral-600 md:text-2xl print:text-xl">
                  {post?.meta?.autore}
                </h4>
              )}

              <p
                className="mt-6 text-base font-light !leading-normal md:text-xl print:text-base print:font-normal"
                dangerouslySetInnerHTML={{
                  __html: post.post_excerpt,
                }}
              />
            </div>
            <div className="mb-8 xl:mb-28 print:mb-0">
              <FeatPost
                imagePost={post.image}
                videoPost={post?.meta?.video}
                audioMultimedia={post?.meta?.audio}
                mainCateogry={post?.category?.main}
                subCategory={post?.category?.sub}
                smallImage={
                  post?.meta?.small_image?.includes("true") ? true : false
                }
              />
            </div>
            <button
              onClick={rotateFontSizes}
              className="flex items-center gap-2.5 transition-all hover:cursor-pointer hover:opacity-75 print:hidden"
            >
              <Image
                width={28}
                height={28}
                src={textSizeIcon}
                alt="Dimensione testo"
              />
              <span className="text-base font-light italic sm:text-lg">
                dimensione del testo
              </span>
            </button>
          </div>
          {hiddenDescription == false && (
            <PostContent
              htmlContent={creaHtmlPost(
                setProtectedUrlPDF(
                  highlightedPost.post_content,
                  post?.protectedUrlPDF
                ),
                note,
                `/a/${highlightedPost.slug}${queryRouterString}`
              )}
              polls={polls}
              textStyle={restrictAccess && textPreviewRule}
              indexArticle={0}
            />
          )}
          {note != null && !restrictAccess && (
            <div className="mx-auto my-8 max-w-section break-words leading-8 max-xl:px-4">
              <span className="font-medium">NOTE</span>
              <ol>
                {note.map((nota, index) => (
                  <li
                    className="py-1 transition-all"
                    key={nota.id}
                    id={nota.id}
                  >
                    <Link
                      href={`/a/${post.slug}${queryRouterString}#${nota.id}-link`}
                      dangerouslySetInnerHTML={{
                        __html: `${index + 1}. `,
                      }}
                    ></Link>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: `${nota.content}`,
                      }}
                    ></span>
                  </li>
                ))}
              </ol>
            </div>
          )}
          {restrictAccess && (
            <div className="is-layout-constrained mx-auto flex w-full max-w-section justify-center py-20 font-light leading-loose max-xl:px-4">
              <div className="mx-6 flex w-full max-w-2xl flex-col justify-center border p-6">
                <h4 className="pb-6 text-center text-lg font-bold">
                  Contenuto riservato agli abbonati
                </h4>
                <div className="flex w-full flex-col justify-center gap-4 sm:flex-row">
                  {session?.status !== "authenticated" && (
                    <a
                      className="m-0 flex w-full items-center justify-center border border-solid  border-brown bg-brown p-4 text-center uppercase text-white lg:w-1/3"
                      href={loginURL}
                    >
                      Accedi
                    </a>
                  )}
                  <a
                    className="!mt-0 flex w-full items-center justify-center border border-orange bg-orange p-4 text-center uppercase text-white lg:w-1/3"
                    href="https://servizi.sgi-italia.org/abbonamenti/index.php/site/abbonamenti"
                  >
                    Sottoscrivi un abbonamento
                  </a>
                </div>
              </div>
            </div>
          )}
          <div className="mx-auto hidden max-w-section px-4 pt-12 print:block print:px-4 print:pt-0">
            <p className="py-6 text-sm text-gray print:text-base">
              <strong>©buddismoesocieta.org</strong> – diritti riservati,
              riproduzione&nbsp;riservata
            </p>
          </div>
        </section>
      </Layout>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const articleByCategory = await getLastArticle();

  return {
    paths: articleByCategory.map((article) => ({
      params: { slug: article.post_name },
    })), //indicates that no page needs be created at build time
    fallback: true, //indicates the type of fallback
  };
};

export const getStaticProps: GetStaticProps<{
  post: ArticleReturnType;
  noIndex: string;
  polls: Poll[];
}> = async ({ params }) => {
  if (!params) return { notFound: true };

  try {
    const post = await getPostInfoBySlug(params.slug as string);
    if (!post) return { notFound: true };
    const noIndex = await PostNoIndex(
      params.slug as string,
      post?.meta?.rank_math_robots as string
    );

    const polls = await getPollsFromContent(post.post_content);

    return {
      props: {
        post,
        noIndex,
        polls: polls != null ? polls : [],
      },
    };
  } catch (error) {
    console.log("Error fetching static props for", params.slug, error);
    return { notFound: true };
  }
};
